import { useAction, useAtom } from '@reatom/npm-react'
import BigNumber from 'bignumber.js'

import { AsyncStateN } from '@blade/std-lib'
import { Numeric } from '@blade/ui-kit'
import { distributeRangeAtom } from '@root/features/profile/distribute-drawer/atoms'

import { Distribution } from './distribution'
import { DistributionError } from './distribution-error'
import { DistributionSkeleton } from './distribution-skeleton'

import type { UserTradingAnalyticsModel } from '../../atoms'
import type { UserTradingAnalyticsPerformancePnlDistribution } from '../../models/user-trading-analytics-performance'
import type { DistributeRange } from '@root/features/profile/distribute-drawer/model'

type PhishingCheckBlockProps = {
  model: UserTradingAnalyticsModel
}

export const DistributionBlock = ({ model }: PhishingCheckBlockProps) => {
  const [analyticsState] = useAtom(model.requestStateAtom)

  const [, setDistributeRange] = useAtom(distributeRangeAtom)

  const onRetry = useAction(model.requestStateAtom.refresh)
  const onDistributionClick = () => setDistributeRange([-Infinity, Infinity])

  return AsyncStateN.matchWaiting(analyticsState, {
    Success: (data) => {
      const { performance } = data

      return (
        <Distribution
          distribution={mapDistribution(performance.pnlDistribution)}
          onClick={onDistributionClick}
        />
      )
    },
    Error: () => <DistributionError onRetry={onRetry} />,
    Waiting: () => <DistributionSkeleton />,
  })
}

const mapDistribution = (
  distribution: UserTradingAnalyticsPerformancePnlDistribution,
): ReadonlyArray<{
  range: DistributeRange
  color: string
  border: string
  count: string
  percentage: React.ReactNode
  percentageValue: number | undefined
}> => {
  const sum = BigNumber(distribution.belowNeg50)
    .plus(distribution.from0toNeg50)
    .plus(distribution.from0to200)
    .plus(distribution.from200to500)
    .plus(distribution.over500)

  const over500 = BigNumber(distribution.over500).dividedBy(sum)
  const from200to500 = BigNumber(distribution.from200to500).dividedBy(sum)
  const from0to200 = BigNumber(distribution.from0to200).dividedBy(sum)
  const from0toNeg50 = BigNumber(distribution.from0toNeg50).dividedBy(sum)
  const belowNeg50 = BigNumber(distribution.belowNeg50).dividedBy(sum)

  return [
    {
      range: [500, Infinity],
      color: 'var(--up-10)',
      border: 'rgba(var(--up-11-rgb), 0.9)',
      count: distribution.over500.toString(),
      percentage: Number.isNaN(over500.toNumber()) ? undefined : (
        <>
          (
          <Numeric value={over500.multipliedBy(100)} preset="percents" />
          %)
        </>
      ),
      percentageValue: over500.toNumber(),
    },
    {
      range: [200, 500],
      color: 'var(--up-8)',
      border: 'rgba(var(--up-11-rgb), 0.3)',
      count: distribution.from200to500.toString(),
      percentage: Number.isNaN(from200to500.toNumber()) ? undefined : (
        <>
          (
          <Numeric value={from200to500.multipliedBy(100)} preset="percents" />
          %)
        </>
      ),
      percentageValue: from200to500.toNumber(),
    },
    {
      range: [0, 200],
      color: 'var(--up-5)',
      border: 'var(--up-6)',
      count: distribution.from0to200.toString(),
      percentage: Number.isNaN(from0to200.toNumber()) ? undefined : (
        <>
          (
          <Numeric value={from0to200.multipliedBy(100)} preset="percents" />
          %)
        </>
      ),
      percentageValue: from0to200.toNumber(),
    },
    {
      range: [-50, 0],
      color: 'var(--down-7)',
      border: 'var(--down-8)',
      count: distribution.from0toNeg50.toString(),
      percentage: Number.isNaN(from0toNeg50.toNumber()) ? undefined : (
        <>
          (
          <Numeric value={from0toNeg50.multipliedBy(100)} preset="percents" />
          %)
        </>
      ),
      percentageValue: from0toNeg50.toNumber(),
    },
    {
      range: [-Infinity, -50],
      color: 'var(--down-9)',
      border: 'rgba(var(--down-11-rgb), 0.6)',
      count: distribution.belowNeg50.toString(),
      percentage: Number.isNaN(belowNeg50.toNumber()) ? undefined : (
        <>
          (
          <Numeric value={belowNeg50.multipliedBy(100)} preset="percents" />
          %)
        </>
      ),
      percentageValue: belowNeg50.toNumber(),
    },
  ]
}
